import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import FullScreenImageViewer, { isImageAttachment } from "./FullScreenImageViewer";
import { parseArrayData, getCommunicationNotes, addCommunicationNote, normalizePriority } from "../pages/TenantAuth";
import { enumOf } from "../../utils/enums";

// Shared maintenance-request detail modal (Details / Conversation) used by the dashboard and the maintenance page.

function capitalizeFirst(str) {
  const t = String(str || "").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  });
}

function getInitials(first = "", last = "") {
  if (first && last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  const clean = (first || last || "").replace(/^(mr|mrs|ms|miss|dr|prof|engr|arch|barr|alh|alhaji)\.?\s+/i, "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  return (clean.slice(0, 2) || "??").toUpperCase();
}

function useBootstrapModal(ref) {
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { hide };
}

function ModalShell({ modalRef, icon, title, onClose, closeDisabled, subheader, footer, overlay, width = 720, fill = false, children }) {
  return (
    <div className="modal fade" ref={modalRef} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: width }}>
        <div className="modal-content d6m">
          {overlay}
          <div className="d6m-head">
            <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
            <div className="min-w-0 flex-grow-1">
              <h3 className="d6m-title text-truncate">{title}</h3>
            </div>
            <button type="button" className="d6m-x" onClick={onClose} disabled={closeDisabled} aria-label="Close">
              <i className="ki-outline ki-cross"></i>
            </button>
          </div>
          {subheader}
          <div className={`d6m-body ${fill ? "d6m-body-fill" : ""}`}>{children}</div>
          {footer && <div className="d6m-foot">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ color = "#a1a1aa", children }) {
  return (
    <span className="d6m-chip" style={{ "--dot": color }}>
      {children}
    </span>
  );
}

function Avatar({ src, name, size = 48, onClick }) {
  const initials = getInitials(name || "");
  return (
    <span
      className={`d6m-avatar ${onClick ? "cursor-pointer" : ""}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
      onClick={onClick}
      title={onClick ? "View photo" : undefined}
    >
      {src ? <img src={src} alt="" /> : initials}
    </span>
  );
}

const Shimmer = ({ w = "100%", h = 12, r = 6, className = "" }) => (
  <span className={`d6m-shimmer ${className}`} style={{ width: w, height: h, borderRadius: r }} />
);

export const REQUEST_DOT = { Open: "#D97706", "In progress": "#2563EB", Resolved: "#16A34A", Canceled: "#a1a1aa" };
export const PRIORITY_DOT = { Low: "#0891B2", Medium: "#a1a1aa", Normal: "#a1a1aa", High: "#D97706", Critical: "#DC2626" };

function normalizeRequestStatus(status) {
  const s = String(status || "").toLowerCase();
  if (/resolve|closed|complete/.test(s)) return "Resolved";
  if (/progress|assign/.test(s)) return "In progress";
  if (/cancel|reject/.test(s)) return "Canceled";
  return "Open";
}

export function normalizeRequest(r) {
  return {
    id: r.id || r.maintenanceRequestId,
    title: capitalizeFirst(r.title || "Maintenance request"),
    description: capitalizeFirst(r.description || ""),
    category: r.category || r.categoryName || "General",
    priority: normalizePriority(enumOf(r, "priorityLevel") ?? r.priority),
    status: normalizeRequestStatus(enumOf(r, "status")),
    createdDate: r.createdDate,
    home: r.buildingName || "",
    uploadedFileUrls: r.uploadedFileUrls || [],
  };
}

const mapNote = (n) => ({
  id: n.id,
  mine: String(n.role || enumOf(n, "senderType") || "").toLowerCase() !== "admin" && !n.isFromAdmin,
  name: n.senderName || n.createdByName,
  message: n.note || n.message,
  createdDate: n.createdDate || n.createdAt,
});

export function RequestModal({ modalRef, request, onClose }) {
  const { hide } = useBootstrapModal(modalRef);
  const [tab, setTab] = useState("details");
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [viewer, setViewer] = useState({ isOpen: false, images: [], initialIndex: 0, title: "" });
  const endRef = useRef(null);

  useEffect(() => {
    if (!request?.id) return undefined;
    let alive = true;
    setTab("details");
    setLoading(true);
    getCommunicationNotes(request.id)
      .then((res) => {
        if (!alive) return;
        setNotes(parseArrayData({ data: res }).map(mapNote));
      })
      .catch(() => { if (alive) setNotes([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [request]);

  useEffect(() => {
    if (tab === "thread" && notes.length) endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [notes, tab]);

  const handleClose = () => { hide(); setReplyText(""); onClose?.(); };

  const handleSend = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await addCommunicationNote(request.id, { message: replyText.trim() });
      const res = await getCommunicationNotes(request.id);
      setNotes(parseArrayData({ data: res }).map(mapNote));
      setReplyText("");
      setJustSent(true);
      setTimeout(() => setJustSent(false), 1800);
    } catch (err) {
      toast.error(err.message || "Could not send your message.");
    } finally {
      setSending(false);
    }
  };

  const r = request;
  const images = (r?.uploadedFileUrls || []).filter(isImageAttachment);

  return (
    <>
      <ModalShell
        modalRef={modalRef}
        icon="ki-wrench"
        title={r?.title || "Maintenance request"}
        onClose={handleClose}
        width={760}
        fill={tab === "thread"}
        subheader={
          r && (
            <div className="d6m-tabs">
              <button type="button" className={tab === "details" ? "active" : ""} onClick={() => setTab("details")}>Details</button>
              <button type="button" className={tab === "thread" ? "active" : ""} onClick={() => setTab("thread")}>
                Conversation{notes.length > 0 && <span className="d6m-count ms-2">{notes.length}</span>}
              </button>
            </div>
          )
        }
        footer={<button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>Close</button>}
      >
        {!r ? (
          <div className="d6m-empty">No request selected.</div>
        ) : tab === "details" ? (
          loading ? (
            <>
              <div className="d6m-kv d6m-kv-4">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="d6m-kv-cell"><Shimmer w="50%" h={10} /><Shimmer w="70%" h={14} className="mt-2" /></div>
                ))}
              </div>
              <div className="d6m-section"><Shimmer w="30%" h={12} /><Shimmer w="100%" h={12} className="mt-3" /><Shimmer w="80%" h={12} className="mt-2" /></div>
            </>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-between gap-3 mb-5">
                <div className="d6m-muted">Reported {fmtDateTime(r.createdDate)}</div>
                <StatusChip color={REQUEST_DOT[r.status]}>{r.status}</StatusChip>
              </div>
              <div className="d6m-kv d6m-kv-4">
                <div className="d6m-kv-cell"><span className="d6m-k">Home</span><span className="d6m-v text-truncate d-block">{r.home || "—"}</span></div>
                <div className="d6m-kv-cell"><span className="d6m-k">Category</span><span className="d6m-v">{r.category}</span></div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Priority</span>
                  <span className="d6m-v d-flex align-items-center gap-2">
                    <span className="d6m-dot" style={{ background: PRIORITY_DOT[r.priority] || "#a1a1aa" }} />
                    {r.priority}
                  </span>
                </div>
                <div className="d6m-kv-cell"><span className="d6m-k">Reference</span><span className="d6m-v">{String(r.id).toUpperCase()}</span></div>
              </div>

              <div className="d6m-section">
                <div className="d6m-section-head"><span>What you reported</span></div>
                <div className="d6m-issue-title">{r.title}</div>
                {r.description && r.description.toLowerCase() !== r.title.toLowerCase() && <p className="d6m-issue-desc">{r.description}</p>}
              </div>

              {images.length > 0 && (
                <div className="d6m-section">
                  <div className="d6m-section-head"><span>Photos</span><span className="d6m-count">{images.length}</span></div>
                  <div className="d6m-attachments">
                    {images.map((url, i) => (
                      <button key={url} type="button" className="d6m-thumb" onClick={() => setViewer({ isOpen: true, images, initialIndex: i, title: r.title })}>
                        <img src={url} alt="" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="d6m-note">
                <i className="ki-outline ki-information-5"></i>
                <span>The estate team updates the status as they work on it. Use the conversation tab to add details or ask for an update.</span>
              </div>
            </>
          )
        ) : (
          <>
            <div className="d6m-thread">
              {loading ? (
                <div className="d6m-empty"><span className="spinner-border spinner-border-sm me-2 text-gray-400"></span>Loading messages…</div>
              ) : notes.length === 0 ? (
                <div className="d6m-empty">
                  <i className="ki-outline ki-messages fs-2x text-gray-300 d-block mb-2"></i>
                  No messages yet. Send the estate team a note below.
                </div>
              ) : (
                notes.map((n, i) => (
                  <div key={n.id} className={`d6m-msg ${n.mine ? "mine" : ""} ${justSent && i === notes.length - 1 ? "d6-bubble-in" : ""}`}>
                    {!n.mine && <Avatar name={n.name || "Estate team"} size={28} />}
                    <div className="d6m-msg-body">
                      <div className="d6m-msg-meta"><span>{n.mine ? "You" : n.name || "Estate team"}</span><span>{fmtDateTime(n.createdDate)}</span></div>
                      <div className="d6m-bubble">{n.message}</div>
                    </div>
                  </div>
                ))
              )}
              <div ref={endRef} />
            </div>
            <div className="d6m-composer">
              <textarea
                rows={2}
                placeholder="Write a message to the estate team…"
                value={replyText}
                onChange={(e) => {
                  const val = e.target.value;
                  setReplyText(val.length === 1 ? val.toUpperCase() : val);
                }}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSend(); }}
              />
              <button
                type="button"
                className={`btn btn-success d6-mbtn flex-shrink-0 ${justSent ? "d6-mbtn-sent" : ""}`}
                onClick={handleSend}
                disabled={(!replyText.trim() && !justSent) || sending}
              >
                {sending ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : justSent ? (
                  <><svg viewBox="0 0 24 24" className="d6-mini-tick me-2" aria-hidden="true"><path d="M5 12.5 L10 17.5 L19 7.5" /></svg>Sent</>
                ) : (
                  "Send"
                )}
              </button>
            </div>
          </>
        )}
      </ModalShell>
      <FullScreenImageViewer
        isOpen={viewer.isOpen}
        images={viewer.images}
        initialIndex={viewer.initialIndex}
        title={viewer.title}
        onClose={() => setViewer((v) => ({ ...v, isOpen: false }))}
      />
    </>
  );
}

